import {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react'

import {
  calculateVisibleCount,
  calculateNavigationIndex,
} from '../utils/tab-navigation.util'

export function useTabNavigation(items: string[]) {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureButtonsRef = useRef<Nullable<HTMLButtonElement>[]>([])
  const visibleButtonsContainerRef = useRef<HTMLDivElement>(null)
  const arrowButtonsRef = useRef<{
    left: Nullable<HTMLButtonElement>
    right: Nullable<HTMLButtonElement>
  }>({ left: null, right: null })
  const [visibleStartIndex, setVisibleStartIndex] = useState(0)
  const [visibleCount, setVisibleCount] = useState(1)
  const isCalculatingRef = useRef(false)
  const lastCalculatedWidthRef = useRef(0)
  const lastVisibleCountRef = useRef(1)

  const calculateAndSetVisibleCount = useCallback(() => {
    if (isCalculatingRef.current) return

    if (!containerRef.current || !visibleButtonsContainerRef.current) {
      setVisibleCount(1)
      return
    }

    const validButtons = measureButtonsRef.current.filter(
      btn => btn !== null && btn.offsetWidth > 0
    )

    if (validButtons.length === 0 || validButtons.length < items.length) {
      return
    }

    const containerRect = containerRef.current.getBoundingClientRect()
    const containerWidth = containerRect.width

    if (containerWidth <= 0) {
      setVisibleCount(1)
      return
    }

    if (Math.abs(containerWidth - lastCalculatedWidthRef.current) < 1) {
      return
    }

    lastCalculatedWidthRef.current = containerWidth

    const buttonWidths = validButtons.map(btn => {
      const rect = btn!.getBoundingClientRect()
      return rect.width
    })

    if (buttonWidths.length === 0 || buttonWidths.length < items.length) {
      return
    }

    const actualArrowWidth = arrowButtonsRef.current.left?.offsetWidth || 28

    isCalculatingRef.current = true

    const count = calculateVisibleCount(
      containerWidth,
      buttonWidths,
      actualArrowWidth
    )
    const maxCount = Math.min(count, items.length)
    const newVisibleCount = Math.max(1, maxCount)

    if (newVisibleCount !== lastVisibleCountRef.current) {
      lastVisibleCountRef.current = newVisibleCount
      setVisibleCount(newVisibleCount)
    }

    setTimeout(() => {
      isCalculatingRef.current = false
    }, 0)
  }, [items.length])

  useLayoutEffect(() => {
    if (!containerRef.current || items.length === 0) {
      setVisibleCount(1)
      setVisibleStartIndex(0)
      return
    }

    const timeoutId = setTimeout(() => {
      calculateAndSetVisibleCount()
    }, 100)

    const intervalId = setInterval(() => {
      const validButtons = measureButtonsRef.current.filter(
        btn => btn !== null && btn.offsetWidth > 0
      )
      if (validButtons.length >= items.length) {
        calculateAndSetVisibleCount()
        clearInterval(intervalId)
      }
    }, 50)

    const resizeObserver = new ResizeObserver(() => {
      lastCalculatedWidthRef.current = 0
      calculateAndSetVisibleCount()
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      clearTimeout(timeoutId)
      clearInterval(intervalId)
      resizeObserver.disconnect()
    }
  }, [items.length, items, calculateAndSetVisibleCount])

  useLayoutEffect(() => {
    if (
      containerRef.current &&
      visibleButtonsContainerRef.current &&
      items.length > 0 &&
      visibleCount > 0 &&
      !isCalculatingRef.current
    ) {
      const buttonsContainer = visibleButtonsContainerRef.current
      const buttonsScrollWidth = buttonsContainer.scrollWidth
      const buttonsOffsetWidth = buttonsContainer.offsetWidth

      if (buttonsScrollWidth > buttonsOffsetWidth && visibleCount > 1) {
        const adjustedCount = Math.max(1, visibleCount - 1)
        if (
          adjustedCount !== visibleCount &&
          adjustedCount !== lastVisibleCountRef.current
        ) {
          lastVisibleCountRef.current = adjustedCount
          setVisibleCount(adjustedCount)
        }
      }
    }
  }, [visibleStartIndex, visibleCount, items.length, items])

  useEffect(() => {
    setVisibleStartIndex(0)
  }, [items.length])

  const visibleItems = useMemo(() => {
    if (items.length === 0) return []
    if (visibleCount === 0) {
      return items.slice(0, Math.min(1, items.length))
    }
    return items.slice(visibleStartIndex, visibleStartIndex + visibleCount)
  }, [items, visibleStartIndex, visibleCount])

  const showLeftArrow = visibleStartIndex > 0
  const showRightArrow = useMemo(() => {
    if (items.length === 0) return false
    if (visibleCount === 0) return items.length > 1
    return visibleStartIndex + visibleCount < items.length
  }, [items.length, visibleStartIndex, visibleCount])

  const handleNavigate = useCallback(
    (direction: 'left' | 'right') => {
      if (
        (direction === 'left' && showLeftArrow) ||
        (direction === 'right' && showRightArrow)
      ) {
        const newIndex = calculateNavigationIndex(
          visibleStartIndex,
          direction,
          visibleCount,
          items.length
        )
        setVisibleStartIndex(newIndex)
      }
    },
    [
      visibleStartIndex,
      visibleCount,
      items.length,
      showLeftArrow,
      showRightArrow,
    ]
  )

  return {
    containerRef,
    measureButtonsRef,
    visibleButtonsContainerRef,
    arrowButtonsRef,
    visibleItems,
    showLeftArrow,
    showRightArrow,
    handleNavigate,
  }
}
